import { ProposalContent, ProposalImages, AdditionalPhoto } from "@/types/proposal";
import { SectionHeader } from "./SectionHeader";
import { ImageModal } from "./ImageModal";
import { DraggableImageFrame } from "./DraggableImageFrame";

interface ProposalPage2Props {
  content: ProposalContent;
  images: ProposalImages;
  additionalPhotos: AdditionalPhoto[];
  onOptionImageDrop?: (optionId: string, type: 'main' | 'vanity', dataUrl: string) => void;
  onFloorPlanImageDrop?: (dataUrl: string) => void;
  isClientMode?: boolean;
}

const StyleBlock = ({ 
  title, 
  mainImg, 
  descTitle, 
  descText,
  price,
  vanityImg,
  vanityDescTitle,
  vanityDescText,
  optionId,
  onMainImageDrop,
  onVanityImageDrop,
}: { 
  title: string; 
  mainImg: string | null; 
  descTitle: string; 
  descText: string;
  price?: string;
  vanityImg?: string | null;
  vanityDescTitle?: string;
  vanityDescText?: string;
  optionId?: string;
  onMainImageDrop?: (dataUrl: string) => void;
  onVanityImageDrop?: (dataUrl: string) => void;
}) => (
  <div className="space-y-4">
    <div className="text-center">
      <h3 className="text-xl font-medium text-slate-800 mb-3 tracking-tight truncate h-7">{title}</h3>
        <div className="mb-3">
        {onMainImageDrop ? (
          <DraggableImageFrame
            image={mainImg}
            alt={title}
            placeholder={title.toUpperCase()}
            onImageDrop={onMainImageDrop}
          />
        ) : mainImg ? (
          <div 
            className="cab-thumb bg-white p-0 shadow-lg h-64 print:h-48 flex items-center justify-center overflow-hidden"
            draggable={onMainImageDrop !== undefined}
            onDragStart={(e) => {
              if (mainImg && onMainImageDrop !== undefined) {
                e.dataTransfer.setData('application/x-gallery-image', mainImg);
                e.dataTransfer.effectAllowed = 'copy';
              }
            }}
          >
            <ImageModal src={mainImg} alt={title} className="w-full h-full object-cover object-center block cursor-pointer" containerClassName="w-full h-full" />
          </div>
        ) : null}
        <div className="mt-3 text-left min-h-[6rem] md:min-h-[8rem]">
          <h4 className="text-base font-medium text-slate-800 mb-2 tracking-tight">{descTitle}</h4>
          <p className="text-slate-600 text-sm leading-relaxed">{descText}</p>
          {price && price.trim() && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-lg font-bold text-slate-900">{price}</p>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Vanity section - only show if there's actual vanity content */}
    {vanityDescTitle && vanityDescTitle.trim() && vanityDescText && vanityDescText.trim() && (
      <div className="text-center">
        <div className="mb-3">
          {onVanityImageDrop ? (
            <DraggableImageFrame
              image={vanityImg || null}
              alt={`${title} Vanity`}
              placeholder="VANITY"
              onImageDrop={onVanityImageDrop}
            />
          ) : vanityImg ? (
            <div 
              className="cab-thumb bg-white p-0 shadow-lg h-64 print:h-48 flex items-center justify-center overflow-hidden"
              draggable={onVanityImageDrop !== undefined}
              onDragStart={(e) => {
                if (vanityImg && onVanityImageDrop !== undefined) {
                  e.dataTransfer.setData('application/x-gallery-image', vanityImg);
                  e.dataTransfer.effectAllowed = 'copy';
                }
              }}
            >
              <ImageModal src={vanityImg} alt={`${title} Vanity`} className="w-full h-full object-cover object-center block cursor-pointer" containerClassName="w-full h-full" />
            </div>
          ) : null}
          <div className="mt-3 text-left">
            <h4 className="text-base font-medium text-slate-800 mb-2 tracking-tight">{vanityDescTitle}</h4>
            <p className="text-slate-600 text-sm leading-relaxed">{vanityDescText}</p>
          </div>
        </div>
      </div>
    )}
  </div>
);

const ProjectGallery = ({ photos, isClientMode }: { photos: AdditionalPhoto[]; isClientMode?: boolean }) => {
  // Filter photos based on client mode
  const displayPhotos = isClientMode 
    ? photos.filter(p => !p.isInspiration && p.showInClient !== false)
    : photos;

  if (!displayPhotos?.length) return null;

  const handleDragStart = (e: React.DragEvent, photo: AdditionalPhoto) => {
    e.dataTransfer.setData('image/dataurl', photo.dataUrl);
    e.dataTransfer.effectAllowed = 'copy';
  };
  
  // Separate inspiration and project photos for builder view
  const projectPhotos = displayPhotos.filter(p => !p.isInspiration);
  const inspirationPhotos = displayPhotos.filter(p => p.isInspiration);
  
  return (
    <div className="mt-12 space-y-8 print:hidden">
      {/* Project Gallery */}
      {projectPhotos.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-slate-800 tracking-tight text-center border-t border-slate-200 pt-8">
            PROJECT GALLERY
          </h3>
          {!isClientMode && (
            <p className="text-xs text-slate-500 text-center -mt-2">Drag photos to the frames above • These are visible to clients</p>
          )}
          <div className="grid grid-cols-2 gap-6">
            {projectPhotos.map((p) => (
              <div 
                key={p.id} 
                className="space-y-2 cursor-move hover:opacity-75 transition-opacity"
                draggable
                onDragStart={(e) => handleDragStart(e, p)}
              >
                <div className="bg-slate-50 p-4 shadow-lg">
                  <ImageModal src={p.dataUrl} alt={p.caption} className="w-full h-48 object-cover object-center block" />
                </div>
                {p.caption && (
                  <p className="text-slate-600 text-sm text-center leading-relaxed">{p.caption}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inspiration Gallery - Only in builder view */}
      {!isClientMode && inspirationPhotos.length > 0 && (
        <div className="space-y-4">
          <div className="border-t-2 border-amber-300 pt-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-2xl">📌</span>
              <h3 className="text-lg font-medium text-amber-900 tracking-tight text-center">
                INSPIRATION GALLERY
              </h3>
            </div>
            <p className="text-xs text-amber-700 text-center -mt-2 mb-4 bg-amber-50 inline-block px-4 py-1 rounded-full mx-auto block w-fit">
              Reference images • Hidden from client
            </p>
          </div>
          <div className="grid grid-cols-2 gap-6 bg-amber-50 p-6 rounded-lg border-2 border-amber-200">
            {inspirationPhotos.map((p) => (
              <div 
                key={p.id} 
                className="space-y-2 cursor-move hover:opacity-75 transition-opacity"
                draggable
                onDragStart={(e) => handleDragStart(e, p)}
              >
                <div className="bg-white p-4 shadow-lg border-2 border-amber-200">
                  <ImageModal src={p.dataUrl} alt={p.caption} className="w-full h-48 object-cover object-center block" />
                </div>
                {p.caption && (
                  <p className="text-amber-900 text-sm text-center leading-relaxed font-medium">{p.caption}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ProposalPage2 = ({ content, images, additionalPhotos, onOptionImageDrop, onFloorPlanImageDrop, isClientMode }: ProposalPage2Props) => {
  const floorPlan = onFloorPlanImageDrop ? (
    <DraggableImageFrame
      image={images.floorPlan}
      alt="Kitchen Floor Plan"
      placeholder="FLOOR PLAN"
      onImageDrop={onFloorPlanImageDrop}
    />
  ) : images.floorPlan ? (
    <ImageModal src={images.floorPlan} alt="Kitchen Floor Plan" className="w-full h-96 object-contain print:max-h-48" />
  ) : (
    <div className="w-full h-96 print:h-64 bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
      <span className="text-slate-400 text-sm tracking-wide">FLOOR PLAN</span>
    </div>
  );

  // Determine grid layout based on number of options
  const numberOfOptions = content.numberOfOptions || content.cabinetOptions.length;
  const gridClass = numberOfOptions === 1 
    ? "grid-cols-1 max-w-2xl mx-auto" 
    : numberOfOptions === 2 
    ? "grid-cols-2" 
    : numberOfOptions === 3
    ? "grid-cols-3"
    : "grid-cols-2 lg:grid-cols-4";

  return (
    <div className="proposal-page is-page-2 avoid-break w-full mx-auto bg-white shadow-xl print:shadow-none print:rounded-none print:mb-0 mb-8 p-8 print:p-4">
      <div className="avoid-break">
        <SectionHeader 
          title="Design Options & Layout" 
          right={<div className="text-sm text-slate-500">PROJECT {content.projectNumber} | PAGE 02</div>} 
        />
      </div>

      <div className="flex-1">
        <div className={`grid ${gridClass} gap-8 mb-12 avoid-break`}>
          {content.cabinetOptions.map((option) => (
            <div key={option.id} className="avoid-break">
              <StyleBlock 
                title={option.title.toUpperCase()}
                mainImg={images.optionImages[option.id] || null}
                descTitle={option.title}
                descText={option.description}
                price={option.price}
                vanityImg={images.vanityImages[option.id] || null}
                vanityDescTitle={option.vanityTitle}
                vanityDescText={option.vanityDescription}
                optionId={option.id}
                onMainImageDrop={onOptionImageDrop ? (dataUrl) => onOptionImageDrop(option.id, 'main', dataUrl) : undefined}
                onVanityImageDrop={onOptionImageDrop ? (dataUrl) => onOptionImageDrop(option.id, 'vanity', dataUrl) : undefined}
              />
            </div>
          ))}
        </div>

        <div className="space-y-6 border-t-2 border-slate-200 pt-8 avoid-break">
          <div className="text-center">
            <div className="inline-block bg-slate-800 text-white px-6 py-2 text-sm font-bold tracking-widest uppercase mb-4">
              Architectural Layout
            </div>
          </div>
          
          {(onFloorPlanImageDrop || images.floorPlan) && (
            <div className="avoid-break bg-slate-50 p-8 print:p-4 shadow-xl print:shadow-none rounded-lg print:rounded-none border-2 border-slate-200 print:max-h-64">{floorPlan}</div>
          )}
          
          <div className="avoid-break bg-slate-100 border-l-4 border-amber-400 p-6 shadow-lg print:shadow-none">
            <p className="text-slate-700 text-base leading-relaxed mb-4 font-medium">
              {content.floorPlanDescription}
            </p>
            
            {(content.onlinePlansLink && content.onlinePlansLink.trim()) && (
              <div className="bg-white p-4 border border-slate-300 shadow-md print:shadow-none">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                      📋 Full Documentation
                    </div>
                    <div className="text-sm text-slate-600 font-medium">
                      View complete technical plans and specifications online
                    </div>
                  </div>
                  <a 
                    href={content.onlinePlansLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="ml-4 bg-slate-800 text-white px-6 py-3 hover:bg-amber-500 transition-all font-bold tracking-wide uppercase text-xs shadow-lg whitespace-nowrap"
                  >
                    View Plans →
                  </a>
                </div>
              </div>
            )}
          </div>

          {(content.sketchupLink && content.sketchupLink.trim()) && (
            <div className="relative avoid-break interactive-3d print:hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 mt-12 text-center shadow-2xl border-4 border-amber-400 overflow-hidden">
              <div className="relative z-10">
                <div className="inline-block bg-amber-400 text-slate-900 px-4 py-1 text-xs font-bold tracking-widest mb-4 uppercase">
                  Interactive Experience
                </div>
                <h4 className="text-2xl font-bold text-white mb-3 tracking-tight">
                  🏠 VIEW YOUR DESIGN IN 3D
                </h4>
                <p className="text-slate-300 text-base mb-6 max-w-2xl mx-auto leading-relaxed">
                  Experience your kitchen design in immersive 3D. Rotate, zoom, and explore every detail of your custom cabinetry from every angle.
                </p>
                <a 
                  href={content.sketchupLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-block bg-amber-400 text-slate-900 px-10 py-4 hover:bg-amber-300 transition-all font-bold tracking-widest uppercase text-sm shadow-2xl transform hover:scale-105"
                >
                  Launch 3D Model →
                </a>
              </div>
            </div>
          )}
        </div>

        <ProjectGallery photos={additionalPhotos} isClientMode={isClientMode} />
      </div>
    </div>
  );
};